/**
 * Vista "Calendário" — usa o calendário compartilhado do ERP (o mesmo da
 * página Entregas): escalas Dia / Semana / Mês / Ano. Os feriados vêm do
 * calendário da UNIDADE (o mesmo que conta os dias úteis dos prazos), não da
 * lista nacional do navegador — uma lista só. As solicitações entram pela
 * data de EMISSÃO ou pelo PRAZO de
 * entrega vigente (seletor no topo); a cor é a situação; clicar abre o detalhe.
 */
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import Calendar from "../../../components/Calendar";
import { startOfDayLocal } from "../../../components/Calendar/calendarGrid";
import { useAuth } from "../../../context/AuthContext";
import {
  SITUACAO_LABELS, SITUACAO_TONES, obterCalendarioSolicitacoes,
} from "../../../services/solicitacoes";
import { chaveSolicitacao, contagemItens } from "./helpers";

const VOCAB = {
  item: ["solicitação", "solicitações"],
  subitem: ["solicitação", "solicitações"],
  subitemTitulo: "Solicitações",
  grupoTitulo: "Clientes",
  previsao: ["prazo em aberto", "prazos em aberto"],
};

const BASES = [
  { value: "emissao", label: "Emissão" },
  { value: "prazo", label: "Prazo de entrega" },
];

const Wrap = styled.div`
  padding: 2px 2px 24px;
`;

const Barra = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 12px;
  color: var(--ink-3);
`;

const Opcao = styled.button`
  height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid ${({ $on }) => ($on ? "var(--accent)" : "var(--border)")};
  background: ${({ $on }) => ($on ? "var(--accent-soft)" : "var(--surface)")};
  color: ${({ $on }) => ($on ? "var(--accent)" : "var(--ink-2)")};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

// "2026-09-28" (DateField) → Date LOCAL; `new Date(iso)` seria meia-noite UTC
// e cairia no dia anterior em São Paulo.
function dataLocal(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

const ABERTAS = new Set(["ABERTA", "AUTORIZADA", "EM_ANDAMENTO"]);

export function eventosDoCalendario(itens, base) {
  return (itens || []).flatMap((s) => {
    const date = base === "prazo"
      ? dataLocal(s.prazo_entrega_vigente_em)
      : (s.created_at ? startOfDayLocal(new Date(s.created_at)) : null);
    if (!date || Number.isNaN(date.getTime())) return [];
    return [{
      id: chaveSolicitacao(s),
      date,
      title: `${s.numero} · ${contagemItens(s)}`,
      subtitle: s.criado_por_nome || "",
      groupLabel: s.cliente_nome || "Sem cliente",
      tone: SITUACAO_TONES[s.situacao] || "default",
      // Prazo de algo ainda não entregue = promessa em aberto (tracejado).
      previsao: base === "prazo" && ABERTAS.has(s.situacao),
      payload: { ...s, status: SITUACAO_LABELS[s.situacao] || s.situacao },
    }];
  });
}

/** Feriados da unidade do ano em exibição (com folga para a semana da virada). */
function useFeriadosDaUnidade(ano) {
  const { request } = useAuth();
  const [feriados, setFeriados] = useState(null);
  useEffect(() => {
    let vivo = true;
    obterCalendarioSolicitacoes(request, `${ano - 1}-12-01`, `${ano + 1}-01-31`)
      .then((r) => {
        if (!vivo) return;
        setFeriados(Object.fromEntries((r?.feriados || []).map((f) => [f.data, f.nome])));
      })
      // Sem a lista da unidade, o calendário cai nos feriados nacionais.
      .catch(() => { if (vivo) setFeriados(null); });
    return () => { vivo = false; };
  }, [request, ano]);
  return feriados;
}

export default function Calendario({ itens, onAbrir }) {
  const [cursor, setCursor] = useState(() => startOfDayLocal(new Date()));
  const feriados = useFeriadosDaUnidade(cursor.getFullYear());
  const [escala, setEscala] = useState("month");
  const [base, setBase] = useState("emissao");

  const eventos = useMemo(() => eventosDoCalendario(itens, base), [itens, base]);
  const semPrazo = base === "prazo" ? (itens || []).length - eventos.length : 0;

  return (
    <Wrap>
      <Barra>
        <span>Posicionar por</span>
        {BASES.map((b) => (
          <Opcao key={b.value} type="button" $on={base === b.value} onClick={() => setBase(b.value)}>
            {b.label}
          </Opcao>
        ))}
        {semPrazo > 0 && (
          <span title="O prazo nasce na autorização; aguardando autorização e recusadas não têm.">
            · {semPrazo} sem prazo (fora do calendário)
          </span>
        )}
      </Barra>
      <Calendar
        events={eventos}
        cursor={cursor}
        scale={escala}
        onCursorChange={setCursor}
        onScaleChange={setEscala}
        onSelectEvent={(ev) => onAbrir(ev.payload)}
        emptyMessage="Nenhuma solicitação no período com os filtros atuais."
        vocab={VOCAB}
        feriados={feriados}
      />
    </Wrap>
  );
}
